import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";


import axios from "axios";
import { useAuth } from "@/context/AuthContext";

import { motion } from "framer-motion";

import {
  Lock,
  LogIn,
  UserPlus,
  Mail,
  MailCheck,
  KeyRound,
  LogOut,
  ShieldCheck,
} from "lucide-react";

import NotificationPreferences, {
  DEFAULT_NOTIFICATION_PREFERENCES,
} from "@/components/NotificationPreferences";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  "http://127.0.0.1:8000";

export default function Members() {
  const {
    refreshAuth,
    logout: globalLogout,
  } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
const [password, setPassword] = useState("");
const [confirmPassword, setConfirmPassword] = useState("");
const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    ...DEFAULT_NOTIFICATION_PREFERENCES,
  });
  const [creatorProgramInterest, setCreatorProgramInterest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendingVerification, setResendingVerification] =
    useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [creator, setCreator] = useState(null);
  const [storefront, setStorefront] = useState(null);

  // Twitch account integration

  const [kickConnection, setKickConnection] = useState(null);
  const [kickChecking, setKickChecking] = useState(false);
  const [kickLoading, setKickLoading] = useState(false);
  const [kickError, setKickError] = useState("");
  const [kickMessage, setKickMessage] = useState("");

  const [youtubeConnection, setYoutubeConnection] = useState(null);
  const [youtubeChecking, setYoutubeChecking] = useState(false);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeError, setYoutubeError] = useState("");
  const [youtubeMessage, setYoutubeMessage] = useState("");

  const [twitchConnection, setTwitchConnection] = useState(null);
  const [twitchChecking, setTwitchChecking] = useState(false);
  const [twitchLoading, setTwitchLoading] = useState(false);
  const [twitchError, setTwitchError] = useState("");
  const [twitchMessage, setTwitchMessage] = useState("");

  // Restore remembered email when the page loads
  useEffect(() => {
    const savedEmail = localStorage.getItem("doriantelive_remembered_email");

    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const clearMessages = () => {
    setMessage("");
    setError("");
  };

  // --------------------------------------------------
  // Twitch account integration
  // --------------------------------------------------

  const checkTwitchConnection = useCallback(async () => {
    setTwitchChecking(true);
    setTwitchError("");

    try {
      const response = await axios.get(
        `${API_BASE}/api/twitch/status`,
        { withCredentials: true }
      );

      setTwitchConnection(
        response.data?.connected ? response.data : null
      );
    } catch (error) {
      setTwitchConnection(null);

      setTwitchError(
        error.response?.data?.detail ||
        "Unable to check Twitch connection."
      );
    } finally {
      setTwitchChecking(false);
    }
  }, []);

  const handleConnectTwitch = async () => {
    setTwitchLoading(true);
    setTwitchError("");
    setTwitchMessage("");

    try {
      const response = await axios.post(
        `${API_BASE}/api/twitch/connect`,
        {},
        { withCredentials: true }
      );

      const authorizationUrl = response.data?.authorization_url;

      if (!authorizationUrl) {
        throw new Error("Twitch authorization URL was not returned.");
      }

      window.location.assign(authorizationUrl);
    } catch (error) {
      setTwitchError(
        error.response?.data?.detail ||
        error.message ||
        "Unable to connect Twitch."
      );
      setTwitchLoading(false);
    }
  };

  const handleDisconnectTwitch = async () => {
    const confirmed = window.confirm(
      "Disconnect your Twitch account from Doriantelive?"
    );

    if (!confirmed) return;

    setTwitchLoading(true);
    setTwitchError("");
    setTwitchMessage("");

    try {
      await axios.delete(
        `${API_BASE}/api/twitch/disconnect`,
        { withCredentials: true }
      );

      setTwitchConnection(null);
      setTwitchMessage("Twitch account disconnected.");
    } catch (error) {
      setTwitchError(
        error.response?.data?.detail ||
        "Unable to disconnect Twitch."
      );
    } finally {
      setTwitchLoading(false);
    }
  };


  const checkKickConnection = useCallback(async () => {
    setKickChecking(true);
    setKickError("");

    try {
      const response = await axios.get(
        `${API_BASE}/api/kick/status`,
        { withCredentials: true }
      );

      setKickConnection(
        response.data?.connected ? response.data : null
      );
    } catch (err) {
      setKickConnection(null);
      setKickError(
        err.response?.data?.detail ||
        "Unable to check Kick connection."
      );
    } finally {
      setKickChecking(false);
    }
  }, []);

  const handleConnectKick = async () => {
    setKickLoading(true);
    setKickError("");
    setKickMessage("");

    try {
      const response = await axios.post(
        `${API_BASE}/api/kick/connect`,
        {},
        { withCredentials: true }
      );

      const authorizationUrl = response.data?.authorization_url;

      if (!authorizationUrl) {
        throw new Error("Kick authorization URL was not returned.");
      }

      window.location.assign(authorizationUrl);
    } catch (err) {
      setKickError(
        err.response?.data?.detail ||
        err.message ||
        "Unable to connect Kick."
      );
      setKickLoading(false);
    }
  };

  const handleDisconnectKick = async () => {
    if (!window.confirm("Disconnect your Kick account?")) return;

    setKickLoading(true);
    setKickError("");
    setKickMessage("");

    try {
      await axios.delete(
        `${API_BASE}/api/kick/disconnect`,
        { withCredentials: true }
      );

      setKickConnection(null);
      setKickMessage("Kick account disconnected.");
    } catch (err) {
      setKickError(
        err.response?.data?.detail ||
        "Unable to disconnect Kick."
      );
    } finally {
      setKickLoading(false);
    }
  };

  const checkYoutubeConnection = useCallback(async () => {
    setYoutubeChecking(true);
    setYoutubeError("");
    try {
      const response = await axios.get(
        `${API_BASE}/api/youtube/status`,
        { withCredentials: true }
      );
      setYoutubeConnection(
        response.data?.connected ? response.data : null
      );
    } catch (err) {
      setYoutubeConnection(null);
      setYoutubeError(
        err.response?.data?.detail ||
        "Unable to check YouTube connection."
      );
    } finally {
      setYoutubeChecking(false);
    }
  }, []);

  const handleConnectYoutube = async () => {
    setYoutubeLoading(true);
    setYoutubeError("");
    setYoutubeMessage("");
    try {
      const response = await axios.post(
        `${API_BASE}/api/youtube/connect`,
        {},
        { withCredentials: true }
      );
      if (!response.data?.authorization_url) {
        throw new Error("YouTube authorization URL was not returned.");
      }
      window.location.assign(response.data.authorization_url);
    } catch (err) {
      setYoutubeError(
        err.response?.data?.detail ||
        err.message ||
        "Unable to connect YouTube."
      );
      setYoutubeLoading(false);
    }
  };

  const handleDisconnectYoutube = async () => {
    if (!window.confirm("Disconnect your YouTube account?")) return;
    setYoutubeLoading(true);
    setYoutubeError("");
    setYoutubeMessage("");
    try {
      await axios.delete(
        `${API_BASE}/api/youtube/disconnect`,
        { withCredentials: true }
      );
      setYoutubeConnection(null);
      setYoutubeMessage("YouTube account disconnected.");
    } catch (err) {
      setYoutubeError(
        err.response?.data?.detail ||
        "Unable to disconnect YouTube."
      );
    } finally {
      setYoutubeLoading(false);
    }
  };

  const checkSession = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/auth/me`,
        {
          withCredentials: true,
        }
      );

      setUser(response.data.user);
      await refreshAuth();

      // Check Twitch connection after confirming member login.
      await checkTwitchConnection();
      await checkKickConnection();
      await checkYoutubeConnection();

      try {
        const creatorResponse = await axios.get(
          `${API_BASE}/api/creators/me`,
          {
            withCredentials: true,
          }
        );

        setCreator(creatorResponse.data);

        try {
          const storefrontResponse = await axios.get(
            `${API_BASE}/api/storefront/me`,
            {
              withCredentials: true,
            }
          );

          setStorefront(storefrontResponse.data);
        } catch (error) {
          setStorefront(null);
        }

      } catch {
        setCreator(null);
        setStorefront(null);
      }

      return true;
      } catch (error) {
        console.error("checkSession failed:", error);
        setUser(null);
        setCreator(null);
        setStorefront(null);
        await refreshAuth();
        return false;
      }
  }, [refreshAuth, checkTwitchConnection, checkKickConnection, checkYoutubeConnection]);

  useEffect(() => {
    const initializeMembers = async () => {
      const params = new URLSearchParams(
        window.location.search
      );

      const verificationToken = params.get("verify");
      const next = params.get("next");

      if (verificationToken) {
        clearMessages();
        setLoading(true);

        try {
          const response = await axios.post(
            `${API_BASE}/api/auth/verify-email`,
            null,
            {
              params: {
                token: verificationToken,
              },
            }
          );

          setMessage(
            response.data?.message ||
              "Email address verified successfully. You can now log in."
          );
        } catch (err) {
          setError(
            err.response?.data?.detail ||
              "Unable to verify email address."
          );
        } finally {
          const preservedParams = new URLSearchParams();

          if (next === "/stream-presets") {
            preservedParams.set("next", next);
          }

          const query = preservedParams.toString();

          window.history.replaceState(
            {},
            document.title,
            query ? `/members?${query}` : "/members"
          );

          setLoading(false);
        }
      }

      try {
        const sessionValid = await checkSession();

        if (
          sessionValid &&
          next === "/stream-presets"
        ) {
          window.location.replace("/stream-presets");
          return;
        }
      } finally {
        setCheckingSession(false);
      }
    };

    initializeMembers();
  }, [checkSession]);

  const handleLogin = async (event) => {
    event.preventDefault();

    clearMessages();

    setLoading(true);

    try {
      await axios.post(
        `${API_BASE}/api/auth/login`,
        {
          email,
          password,
        },
        {
          withCredentials: true,
        }
      );

      const sessionValid = await checkSession();

      if (!sessionValid) {
        throw new Error("Unable to verify your login session.");
      }

      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");

      if (next === "/stream-presets") {
        window.location.replace("/stream-presets");
        return;
      }

        setMessage("Authentication successful.");

        if (rememberMe) {
          localStorage.setItem("doriantelive_remembered_email", email);
        } else {
          localStorage.removeItem("doriantelive_remembered_email");
        }

        setPassword("");
        } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;

        if (
            status === 403 &&
            detail ===
            "Please verify your email address before logging in."
        ) {
            setMessage(
            "Your email address has not been verified."
            );
        } else {
            setError(
            detail ||
                "Unable to authenticate."
            );
        }
        } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
  clearMessages();
  setResendingVerification(true);

  try {
    const response = await axios.post(
      `${API_BASE}/api/auth/resend-verification`,
      {
        email,
      },
    );

    setMessage(
      response.data?.message ||
        "A new verification email has been sent.",
    );
  } catch (err) {
    setError(
      err.response?.data?.detail ||
        "Unable to resend verification email.",
    );
  } finally {
    setResendingVerification(false);
  }
};

const handleRegister = async (event) => {
    event.preventDefault();

    clearMessages();

    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/api/auth/register`,
        {
          email,
          password,
          notification_preferences: notificationPreferences,
          creator_program_interest: creatorProgramInterest,
        }
      );

      setMessage(
        response.data?.message ||
          "Account created. Please verify your email."
      );

      setMode("login");

      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to create account."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    clearMessages();

    setLoading(true);

    try {
      await globalLogout();

      setUser(null);

      setEmail("");

      setPassword("");

      setMessage("You have been logged out.");
    } catch {
      setError("Unable to complete logout.");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div
        className="
          min-h-screen
          bg-black
          text-white
          font-mono
          flex
          items-center
          justify-center
        "
      >
        <div className="text-center">

          <Lock
            className="
              text-matrix-orange
              mx-auto
              mb-4
              animate-pulse
            "
            size={32}
          />

          <div
            className="
              text-xs
              tracking-[0.35em]
              text-matrix-orange
            "
          >
            DORIANTELIVE // MEMBERS
          </div>

          <div
            className="
              mt-3
              text-sm
              text-white/40
            "
          >
            VERIFYING SESSION...
          </div>

        </div>
      </div>
    );
  }

  if (user) {
    return (
      <div
        className="
          min-h-screen
          bg-black
          text-white
          font-mono
          relative
          overflow-hidden
        "
      >

        <div
          className="
            absolute
            inset-0
            pointer-events-none
            opacity-20
            bg-[radial-gradient(circle_at_center,rgba(255,120,0,0.15),transparent_60%)]
          "
        />

        <div
          className="
            relative
            z-10
            min-h-screen
            flex
            items-center
            justify-center
            px-6
            py-20
          "
        >

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            className="
              w-full
              max-w-2xl
            "
          >

            <div
              className="
                border
                border-matrix-orange/40
                bg-black/80
                backdrop-blur-sm
                p-8
                md:p-10
                shadow-[0_0_40px_rgba(255,120,0,0.08)]
              "
            >

              <div
                className="
                  flex
                  items-center
                  gap-3
                  mb-8
                "
              >

                <ShieldCheck
                  className="
                    text-matrix-orange
                  "
                  size={28}
                />

                <div>

                  <div
                    className="
                      text-xs
                      tracking-[0.3em]
                      text-matrix-orange
                    "
                  >
                    MEMBERS // AUTHENTICATED
                  </div>

                  <h1
                    className="
                      font-display
                      text-3xl
                      md:text-4xl
                      text-white
                    "
                  >
                    MEMBER ACCOUNT
                  </h1>

                </div>

              </div>

              <div
                className="
                  border
                  border-white/10
                  p-6
                  mb-6
                "
              >

                <div
                  className="
                    text-xs
                    tracking-widest
                    text-white/40
                    mb-2
                  "
                >
                  ACCOUNT
                </div>

                <div
                  className="
                    text-lg
                    text-white
                    break-all
                  "
                >
                  {user.email}
                </div>

                <div
                  className="
                    mt-4
                    text-sm
                    text-matrix-green
                  "
                >
                  EMAIL VERIFIED
                </div>

              </div>

              {/* Comik Releaf E-Commerce Partner Promotion */}
              <div className="mb-8 overflow-hidden rounded-xl border border-matrix-green/30 bg-[#0b1010]">

                <div className="border-b border-matrix-green/20 bg-matrix-green/5 px-6 py-5">
                  <div className="mb-3 text-xs tracking-[0.25em] text-matrix-green">
                    COMIK RELEAF // E-COMMERCE PARTNER PROGRAM
                  </div>

                  <h2 className="font-display text-2xl leading-tight text-white md:text-3xl">
                    YOUR BRAND. OUR DESIGNS. YOUR STORE.
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    Launch your own branded e-commerce shop powered by
                    Comik Releaf. No inventory, no printing equipment,
                    and no complicated setup.
                  </p>

                  <p className="mt-3 text-sm leading-7 text-white/60">
                    Get your storefront, connect your domain, and start
                    selling shirts featuring our designs. We handle
                    printing, checkout, shipping, and fulfillment so
                    you can focus on growing your brand.
                  </p>
                </div>

                <div className="p-6">

                  <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-matrix-green/20 bg-matrix-green/5 p-4">
                      <div className="font-display text-3xl text-matrix-green">
                        500
                      </div>
                      <div className="mt-1 text-xs tracking-wider text-white/50">
                        PARTNER SHOPS MAXIMUM
                      </div>
                    </div>

                    <div className="rounded-lg border border-matrix-green/20 bg-matrix-green/5 p-4">
                      <div className="font-display text-3xl text-matrix-green">
                        $149.99
                      </div>
                      <div className="mt-1 text-xs tracking-wider text-white/50">
                        CAD // ONE-TIME ONBOARDING
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 space-y-3 text-sm text-white/75">
                    <p>✓ Your own branded e-commerce storefront</p>
                    <p>✓ Ready-to-sell Comik Releaf designs</p>
                    <p>✓ No inventory or printing equipment required</p>
                    <p>✓ Production, checkout, and shipping handled for you</p>
                    <p>✓ Earn $3 commission per qualifying item sold</p>
                  </div>

                  {creator && (
                    <div className="mb-5 rounded-lg border border-matrix-green/30 bg-matrix-green/10 p-4 text-sm text-matrix-green">
                      EXISTING CREATOR RECORD DETECTED

                      <p className="mt-2 text-xs leading-6 text-white/60">
                        Your account already has a creator record.
                        The new e-commerce onboarding workflow is
                        being prepared separately.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">

                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = "/coming-soon";
                      }}
                      className="
                        w-full
                        rounded-lg
                        border
                        border-matrix-green/60
                        bg-matrix-green
                        px-3
                        py-4
                        text-xs
                        sm:text-sm
                        font-bold
                        tracking-wider
                        text-black
                        transition-colors
                        hover:bg-transparent
                        hover:text-matrix-green
                      "
                    >
                      LAUNCH YOUR E-COMMERCE SHOP
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = "/";
                      }}
                      className="
                        w-full
                        rounded-lg
                        border
                        border-white/30
                        bg-transparent
                        px-3
                        py-4
                        text-xs
                        sm:text-sm
                        font-bold
                        tracking-wider
                        text-white/70
                        transition-colors
                        hover:border-matrix-orange
                        hover:bg-matrix-orange
                        hover:text-black
                      "
                    >
                      <span className="flex flex-col items-center justify-center gap-1 text-center">
                        <span>
                          MAYBE TOMORROW
                        </span>

                        <span className="text-[11px] font-normal normal-case tracking-normal text-white">
                          (continue with the free stuff)
                        </span>
                      </span>
                    </button>

                  </div>

                  <p className="mt-4 text-center text-xs leading-5 text-white/40">
                    Canada and United States only.
                    Shop onboarding and payment activation coming soon.
                  </p>

                </div>
              </div>

              {message && (
                <div
                  className="
                    mb-6
                    text-sm
                    text-matrix-green
                  "
                >
                  {message}
                </div>
              )}

                {mode === "login" &&
                message ===
                    "Your email address has not been verified." && (
                    <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={
                        loading ||
                        resendingVerification ||
                        !email
                    }
                    className="
                        w-full
                        flex
                        items-center
                        justify-center
                        gap-2
                        border
                        border-matrix-orange/50
                        px-5
                        py-3
                        text-sm
                        text-matrix-orange
                        hover:bg-matrix-orange
                        hover:text-black
                        transition-colors
                        disabled:opacity-50
                    "
                    >
                    <MailCheck
                        size={18}
                    />

                    {resendingVerification
                        ? "SENDING VERIFICATION EMAIL..."
                        : "RESEND VERIFICATION EMAIL"}
                    </button>
                )}
                
              {/* TWITCH ACCOUNT CONNECTION */}
              <div className="mb-6 rounded-2xl border border-purple-500/30 bg-[#100b18]/90 p-5 shadow-[0_0_35px_rgba(145,70,255,0.08)]">

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold tracking-[0.15em] text-purple-300">
                      TWITCH INTEGRATION
                    </h3>

                    <p className="mt-1 text-xs text-white/40">
                      Connect your streaming account to Doriantelive.
                    </p>
                  </div>

                  <span
                    className={`rounded-full border px-3 py-1 text-[10px] font-semibold tracking-wider ${
                      twitchConnection
                        ? "border-matrix-green/40 bg-matrix-green/10 text-matrix-green"
                        : "border-white/20 bg-white/5 text-white/40"
                    }`}
                  >
                    {twitchChecking
                      ? "CHECKING..."
                      : twitchConnection
                      ? "CONNECTED"
                      : "NOT CONNECTED"}
                  </span>
                </div>

                {twitchConnection ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                      <p className="text-[11px] uppercase tracking-widest text-white/40">
                        Connected Twitch Account
                      </p>

                      <p className="mt-2 text-base font-semibold text-white">
                        {twitchConnection.twitch_display_name ||
                          twitchConnection.twitch_login}
                      </p>

                      <p className="mt-1 text-xs text-matrix-green">
                        Ready for stream preset updates
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleDisconnectTwitch}
                      disabled={twitchLoading}
                      className="w-full rounded-xl border border-red-500/30 px-5 py-3 text-xs font-semibold tracking-wider text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                    >
                      {twitchLoading
                        ? "DISCONNECTING..."
                        : "DISCONNECT TWITCH"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs leading-6 text-white/60">
                      Authorize Doriantelive to update your Twitch stream
                      title, game category, and tags directly from your
                      saved presets.
                    </p>

                    <button
                      type="button"
                      onClick={handleConnectTwitch}
                      disabled={twitchLoading || twitchChecking}
                      className="w-full rounded-xl border border-purple-400/50 bg-purple-500/15 px-5 py-3 text-xs font-bold tracking-widest text-purple-200 transition-all hover:border-purple-400 hover:bg-purple-500/25 disabled:opacity-50"
                    >
                      {twitchLoading
                        ? "CONNECTING..."
                        : "CONNECT TWITCH"}
                    </button>
                  </div>
                )}

                {twitchError && (
                  <p className="mt-4 text-xs text-red-400">
                    {twitchError}
                  </p>
                )}

                {twitchMessage && (
                  <p className="mt-4 text-xs text-matrix-green">
                    {twitchMessage}
                  </p>
                )}
              </div>

               
              {/* YOUTUBE ACCOUNT CONNECTION */}
              <div className="mb-6 rounded-2xl border border-red-500/30 bg-[#180b0b]/90 p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold tracking-[0.15em] text-red-300">
                      YOUTUBE INTEGRATION
                    </h3>
                    <p className="mt-1 text-xs text-white/40">
                      Connect your YouTube channel to Stream Control.
                    </p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold tracking-wider ${
                    youtubeConnection
                      ? "border-red-400/40 bg-red-400/10 text-red-300"
                      : "border-white/20 bg-white/5 text-white/40"
                  }`}>
                    {youtubeChecking
                      ? "CHECKING..."
                      : youtubeConnection
                      ? "CONNECTED"
                      : "NOT CONNECTED"}
                  </span>
                </div>

                {youtubeConnection ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                      <p className="text-[11px] uppercase tracking-widest text-white/40">
                        Connected YouTube Channel
                      </p>
                      <p className="mt-2 text-base font-semibold text-white">
                        {youtubeConnection.youtube_channel_title ||
                          youtubeConnection.youtube_channel_id}
                      </p>
                      <p className="mt-1 text-xs text-red-300">
                        Ready for broadcast preset updates
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDisconnectYoutube}
                      disabled={youtubeLoading}
                      className="w-full rounded-xl border border-red-500/30 px-5 py-3 text-xs font-semibold tracking-wider text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                    >
                      {youtubeLoading ? "DISCONNECTING..." : "DISCONNECT YOUTUBE"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs leading-6 text-white/60">
                      Authorize Doriantelive to manage your YouTube livestream
                      metadata using saved presets.
                    </p>
                    <button
                      type="button"
                      onClick={handleConnectYoutube}
                      disabled={youtubeLoading || youtubeChecking}
                      className="w-full rounded-xl border border-red-400/50 bg-red-500/15 px-5 py-3 text-xs font-bold tracking-widest text-red-200 transition-all hover:border-red-400 hover:bg-red-500/25 disabled:opacity-50"
                    >
                      {youtubeLoading ? "CONNECTING..." : "CONNECT YOUTUBE"}
                    </button>
                  </div>
                )}

                {youtubeError && (
                  <p className="mt-4 text-xs text-red-400">{youtubeError}</p>
                )}
                {youtubeMessage && (
                  <p className="mt-4 text-xs text-red-300">{youtubeMessage}</p>
                )}
              </div>

              {/* KICK ACCOUNT CONNECTION */}
              <div className="mb-6 rounded-2xl border border-lime-500/30 bg-[#0d170b]/90 p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold tracking-[0.15em] text-lime-300">
                      KICK INTEGRATION
                    </h3>
                    <p className="mt-1 text-xs text-white/40">
                      Connect Kick to Doriantelive Stream Control.
                    </p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold tracking-wider ${
                    kickConnection
                      ? "border-lime-400/40 bg-lime-400/10 text-lime-300"
                      : "border-white/20 bg-white/5 text-white/40"
                  }`}>
                    {kickChecking
                      ? "CHECKING..."
                      : kickConnection
                      ? "CONNECTED"
                      : "NOT CONNECTED"}
                  </span>
                </div>

                {kickConnection ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                      <p className="text-[11px] uppercase tracking-widest text-white/40">
                        Connected Kick Account
                      </p>
                      <p className="mt-2 text-base font-semibold text-white">
                        {kickConnection.kick_slug || kickConnection.kick_user_id}
                      </p>
                      <p className="mt-1 text-xs text-lime-300">
                        Ready for stream preset updates
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDisconnectKick}
                      disabled={kickLoading}
                      className="w-full rounded-xl border border-red-500/30 px-5 py-3 text-xs font-semibold tracking-wider text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                    >
                      {kickLoading ? "DISCONNECTING..." : "DISCONNECT KICK"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs leading-6 text-white/60">
                      Authorize Doriantelive to update your Kick stream title
                      and category directly from saved presets.
                    </p>
                    <button
                      type="button"
                      onClick={handleConnectKick}
                      disabled={kickLoading || kickChecking}
                      className="w-full rounded-xl border border-lime-400/50 bg-lime-500/15 px-5 py-3 text-xs font-bold tracking-widest text-lime-200 transition-all hover:border-lime-400 hover:bg-lime-500/25 disabled:opacity-50"
                    >
                      {kickLoading ? "CONNECTING..." : "CONNECT KICK"}
                    </button>
                  </div>
                )}

                {kickError && (
                  <p className="mt-4 text-xs text-red-400">{kickError}</p>
                )}
                {kickMessage && (
                  <p className="mt-4 text-xs text-lime-300">{kickMessage}</p>
                )}
              </div>

<button
                 type="button"
                 onClick={handleLogout}
                 disabled={loading}
                className="
                  w-full
                  flex
                  items-center
                  justify-center
                  gap-2
                  border
                  border-matrix-orange/50
                  px-5
                  py-3
                  text-matrix-orange
                  hover:bg-matrix-orange
                  hover:text-black
                  transition-colors
                  disabled:opacity-50
                "
              >

                <LogOut
                  size={18}
                />

                {loading
                  ? "LOGGING OUT..."
                  : "LOG OUT"}

              </button>

                <Link
                  to="/"
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white/70 transition-colors hover:border-matrix-orange hover:bg-matrix-orange/10 hover:text-matrix-orange"
                >
                  <span aria-hidden="true">←</span>
                  RETURN TO HOME
                </Link>
            </div>

          </motion.div>

        </div>

      </div>
    );
  }

  return (
    <div
      className="
        min-h-[70vh]
        bg-black
        text-white
        font-mono
        relative
        overflow-hidden
      "
    >

      <div
        className="
          absolute
          inset-0
          pointer-events-none
          opacity-20
          bg-[radial-gradient(circle_at_center,rgba(255,120,0,0.15),transparent_60%)]
        "
      />

      <div
        className="
          relative
          z-10
          min-h-[70vh]
          flex
          items-center
          justify-center
          px-4
          py-24
          sm:px-6
        "
      >

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="
            w-full
            max-w-lg
          "
        >

            <div
              className="
                relative
                overflow-hidden
                rounded-2xl
                border
                border-matrix-green/30
                bg-[#090e0c]/95
                backdrop-blur-xl
                p-6
                sm:p-8
                shadow-[0_0_55px_rgba(0,255,130,0.08)]
              "
            >

            <div
              className="
                text-center
                mb-8
              "
            >

              <div
                className="
                  flex
                  justify-center
                  mb-4
                "
              >

                <Lock
                  className="
                    text-matrix-orange
                  "
                  size={32}
                />

              </div>

              <div
                className="
                  text-xs
                  tracking-[0.35em]
                  text-matrix-orange
                  mb-2
                "
              >
                DORIANTELIVE // MEMBERS
              </div>

              <h1
                className="
                  font-display
                  text-3xl
                  text-white
                "
              >
                {mode === "login"
                  ? "MEMBER LOGIN"
                  : "CREATE ACCOUNT"}
              </h1>

              <p
                className="
                  text-sm
                  text-white/40
                  mt-3
                "
              >
                Your free membership starts here. Access creator tools,
                manage your stream presets, and explore opportunities
                powered by Doriantelive.
              </p>

            </div>

            <div
                className="
                  grid
                  grid-cols-2
                  gap-2
                  rounded-xl
                  border
                  border-white/10
                  bg-white/[0.03]
                  p-1
                  mb-6
                "
            >

              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  clearMessages();
                }}
                className={`
                  flex
                  items-center
                  justify-center
                  gap-2
                  py-3
                  text-sm
                  transition-colors
                  ${
                    mode === "login"
                    ? "bg-matrix-green/15 text-matrix-green border border-matrix-green/50 rounded-lg"
                    : "text-white/50 hover:text-white hover:bg-white/5 border border-transparent rounded-lg"
                  }
                `}
              >

                <LogIn
                  size={16}
                />

                LOGIN

              </button>

              <button
                type="button"
                onClick={() => {
                  setMode("register");
                  clearMessages();
                }}
                className={`
                  flex
                  items-center
                  justify-center
                  gap-2
                  py-3
                  text-sm
                  transition-colors
                  ${
                    mode === "register"
                    ? "bg-matrix-green/15 text-matrix-green border border-matrix-green/50 rounded-lg"
                    : "text-white/50 hover:text-white hover:bg-white/5 border border-transparent rounded-lg"
                  }
                `}
              >

                <UserPlus
                  size={16}
                />

                REGISTER

              </button>

            </div>

            <form
              onSubmit={
                mode === "login"
                  ? handleLogin
                  : handleRegister
              }
              className="
                space-y-5
              "
            >

              <div>

                <label
                  className="
                    block
                    text-xs
                    tracking-widest
                    text-white/40
                    mb-2
                  "
                >
                  EMAIL
                </label>

                <div
                  className="
                    relative
                  "
                >

                  <Mail
                    size={17}
                    className="
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      text-white/30
                    "
                  />

                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    required
                    autoComplete="email"
                    className="
                      w-full
                      bg-black
                      border
                      border-white/15
                      focus:border-matrix-orange
                      outline-none
                      px-10
                      py-3
                      text-white
                      transition-colors
                    "
                    placeholder="yourhandle@???mail.com"
                  />

                </div>

              </div>

              <div>

                <label
                  className="
                    block
                    text-xs
                    tracking-widest
                    text-white/40
                    mb-2
                  "
                >
                  PASSWORD
                </label>

                <div
                  className="
                    relative
                  "
                >

                  <KeyRound
                    size={17}
                    className="
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      text-white/30
                    "
                  />

                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    required
                    minLength={mode === "register" ? 12 : undefined}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    className="
                      w-full
                      bg-black
                      border
                      border-white/15
                      focus:border-matrix-orange
                      outline-none
                      pl-10
                      pr-20
                      py-3
                      text-white
                      placeholder:text-white/30
                      transition-colors
                    "
                    placeholder="Enter password"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((previous) => !previous)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-white/40 transition-colors hover:text-matrix-orange"
                  >
                    {showPassword ? "HIDE" : "SHOW"}
                  </button>

                </div>

              </div>

              {mode === "register" && (
                <div>
                  <label className="mb-2 block text-xs tracking-widest text-white/40">
                    CONFIRM PASSWORD
                  </label>

                  <div className="relative">
                    <KeyRound
                      size={17}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                    />

                    <input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      required
                      minLength={12}
                      autoComplete="new-password"
                      className="w-full bg-black border border-white/15 focus:border-matrix-orange outline-none pl-10 pr-4 py-3 text-white transition-colors"
                      placeholder="Re-enter your password"
                    />
                  </div>

                  {confirmPassword.length > 0 && (
                    <p
                      className={`mt-2 text-xs ${
                        password === confirmPassword
                          ? "text-matrix-green"
                          : "text-red-400"
                      }`}
                    >
                      {password === confirmPassword
                        ? "Passwords match."
                        : "Passwords do not match."}
                    </p>
                  )}
                </div>
              )}

              {mode === "login" && (
                <label className="flex cursor-pointer items-center gap-3 text-sm text-white/60">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    className="h-4 w-4 accent-orange-500"
                  />
                  <span>REMEMBER ME</span>
                </label>
              )}

              {mode === "register" && (
                <div className="space-y-6 border-t border-white/10 pt-6">
                  <NotificationPreferences
                    value={notificationPreferences}
                    onChange={setNotificationPreferences}
                    disabled={loading}
                  />

                  <label className="flex cursor-pointer items-start gap-3 border border-matrix-orange/30 p-4">
                    <input
                      type="checkbox"
                      checked={creatorProgramInterest}
                      onChange={(event) =>
                        setCreatorProgramInterest(event.target.checked)
                      }
                      disabled={loading}
                      className="mt-1 accent-orange-500"
                    />

                    <span>
                      <span className="block text-sm font-semibold text-white">
                        I'm interested in future creator opportunities
                      </span>

                      <span className="mt-2 block text-xs leading-relaxed text-white/50">
                        Keep me informed about upcoming opportunities to earn
                        through the Doriantelive creator program. Joining the
                        interest list is free and doesn't obligate me to participate.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {error && (
                <div
                  className="
                    border
                    border-red-500/30
                    bg-red-500/5
                    p-3
                    text-sm
                    text-red-400
                  "
                >
                  {error}
                </div>
              )}

              {message && (
                <div
                  className="
                    border
                    border-matrix-green/30
                    bg-matrix-green/5
                    p-3
                    text-sm
                    text-matrix-green
                  "
                >
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={
                  loading ||
                  (mode === "register" &&
                    (password.length < 12 ||
                      confirmPassword.length < 12 ||
                      password !== confirmPassword))
                }
                className="
                  w-full
                  flex
                  items-center
                  justify-center
                  gap-2
                  bg-matrix-orange
                  text-black
                  font-bold
                  py-3
                  hover:opacity-90
                  transition-opacity
                  disabled:opacity-50
                "
              >

                {mode === "login" ? (
                  <LogIn
                    size={18}
                  />
                ) : (
                  <UserPlus
                    size={18}
                  />
                )}

                {loading
                  ? "PROCESSING..."
                  : mode === "login"
                    ? "LOGIN"
                    : "CREATE ACCOUNT"}

              </button>

              </form>

              <Link
                to="/"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 px-4 py-3 text-sm font-semibold text-white/70 transition-colors hover:border-matrix-orange hover:bg-matrix-orange/10 hover:text-matrix-orange"
              >
                <span aria-hidden="true">←</span>
                RETURN TO HOME
              </Link>

            </div>

        </motion.div>

      </div>

    </div>
  );
}

